import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Send, HelpCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_INFO = {
  cherche: {
    label: "Je cherche",
    desc: "Vous cherchez un prestataire, partenaire ou client spécifique",
    badge: "cbm-badge-type-cherche",
  },
  propose: {
    label: "Je propose",
    desc: "Vous proposez une offre ou un service",
    badge: "cbm-badge-type-propose",
  },
  opportunite: {
    label: "Opportunité",
    desc: "Vous partagez une opportunité (job, mission, collaboration)",
    badge: "cbm-badge-type-opportunite",
  },
};

const SECTEURS = [
  "Tech & Digital", "Finance & Investissement", "Immobilier", "Conseil & Management",
  "Commerce, Distribution & E-commerce", "Industrie & Manufacturing", "Santé & Pharma", "Éducation & Formation",
  "Transport & Logistique", "Agriculture & Agroalimentaire", "Médias & Communication", "Énergie & Environnement",
  "Tourisme & Hôtellerie", "Juridique & Comptabilité", "BTP & Construction", "Services aux entreprises", "Autre"
];
const VILLES = ["Casablanca", "Rabat", "Marrakech", "Fès", "Tanger", "Agadir", "Meknès", "Oujda", "Kénitra", "Tétouan", "Autre"];

export default function PostOpportunite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const initialType = searchParams.get("type");
  const validTypes = ["cherche", "propose", "opportunite"];
  const [form, setForm] = useState({
    type: validTypes.includes(initialType) ? initialType : "propose",
    titre: searchParams.get("titre") || "",
    description: searchParams.get("description") || "",
    secteur: "",
    ville: "",
    contact: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/profiles/me`, { withCredentials: true })
      .then(({ data }) => {
        setProfile(data);
        if (data) {
          setForm(f => ({ ...f, secteur: data.secteur || "", ville: data.ville || "", contact: data.email || "" }));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) { setError("Le titre est obligatoire"); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/opportunities`, form, { withCredentials: true });
      navigate("/opportunites");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la publication");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingProfile) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="cbm-skeleton h-8 w-40 mb-6 rounded" />
      <div className="cbm-skeleton h-64 w-full rounded-xl" />
    </div>
  );

  if (!profile) return (
    <div className="p-6 max-w-2xl mx-auto animate-fade-in">
      <div className="cbm-card p-8 text-center">
        <p className="text-cbm-secondary text-base font-outfit mb-3">Profil requis</p>
        <p className="text-cbm-muted text-sm mb-5">Vous devez créer votre profil avant de poster une annonce.</p>
        <button className="cbm-btn-primary" onClick={() => navigate("/mon-profil")} data-testid="go-create-profile">
          Créer mon profil
        </button>
      </div>
    </div>
  );

  const restantes = (profile.annonces_max || 4) - (profile.annonces_utilisees || 0);

  return (
    <div className="p-6 max-w-2xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h1 className="cbm-section-title">Poster une annonce</h1>
        <div className="text-right">
          <p className="cbm-label">Quota mensuel</p>
          <p className={`font-outfit font-bold ${restantes <= 0 ? "text-red-400" : restantes <= 1 ? "text-orange-400" : "text-gold"}`}>
            {Math.max(0, restantes)} / {profile.annonces_max} restantes
          </p>
        </div>
      </div>

      {restantes <= 0 ? (
        <div className="cbm-card p-6 text-center">
          <p className="text-red-400 font-outfit text-base mb-2">Limite mensuelle atteinte</p>
          <p className="text-cbm-muted text-sm">Vous avez utilisé toutes vos annonces ce mois-ci.</p>
          {profile.annonces_reset_date && (
            <p className="text-cbm-muted text-sm mt-1">
              Réinitialisation le {new Date(profile.annonces_reset_date).toLocaleDateString("fr-FR")}
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="cbm-card p-6 space-y-5">
          {/* Type selector */}
          <div>
            <label className="cbm-label block mb-3">Type d'annonce *</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(TYPE_INFO).map(([key, info]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`type-${key}`}
                  className={`p-3 rounded-xl border text-center transition-all ${form.type === key ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/20"}`}
                  onClick={() => setForm(f => ({ ...f, type: key }))}
                >
                  <span className={`cbm-badge ${info.badge} mb-1.5`}>{info.label}</span>
                  <p className="text-cbm-muted text-xs leading-tight mt-1.5">{info.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="cbm-label block mb-1.5">Titre *</label>
            <input
              data-testid="field-titre"
              className="cbm-input"
              placeholder="Ex: Recherche développeur React senior pour projet 6 mois"
              value={form.titre}
              onChange={(e) => setForm(f => ({ ...f, titre: e.target.value }))}
              required
              maxLength={150}
            />
          </div>

          {/* Description */}
          <div>
            <label className="cbm-label block mb-1.5">Description</label>
            <textarea
              data-testid="field-description"
              className="cbm-input"
              rows={4}
              placeholder="Détaillez votre annonce : contexte, exigences, budget, délais..."
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="cbm-label block mb-1.5">Secteur</label>
              <select data-testid="field-secteur" className="cbm-input" value={form.secteur} onChange={(e) => setForm(f => ({ ...f, secteur: e.target.value }))}>
                <option value="">Sélectionner</option>
                {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Ville</label>
              <select data-testid="field-ville" className="cbm-input" value={form.ville} onChange={(e) => setForm(f => ({ ...f, ville: e.target.value }))}>
                <option value="">Sélectionner</option>
                {VILLES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Contact */}
          <div>
            <label className="cbm-label block mb-1.5">Contact pour cette annonce</label>
            <input
              data-testid="field-contact"
              className="cbm-input"
              placeholder="Email ou téléphone pour répondre à cette annonce"
              value={form.contact}
              onChange={(e) => setForm(f => ({ ...f, contact: e.target.value }))}
            />
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm" data-testid="post-error">
              {error}
            </div>
          )}

          <button
            data-testid="submit-opportunity-button"
            type="submit"
            disabled={submitting}
            className="cbm-btn-primary w-full justify-center text-sm py-2.5"
          >
            {submitting ? (
              <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Publication...</>
            ) : (
              <><Send size={15} /> Publier l'annonce</>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
